<template>
  <p v-if="!kycs.length" class="text-subtle">Ingen KYC.</p>
  <ul v-else class="kyc-list">
    <li
      v-for="kyc in kycs"
      :key="kyc.kycId"
      class="kyc-row"
      :data-awaiting="kyc.status === 'awaiting_answers' ? 'true' : 'false'"
    >
      <div class="kyc-row-header">
        <AppChip :color="statusChipColor(kyc.status)" dot>{{ translateRef('kycStatuses', kyc.status) }}</AppChip>
        <span v-if="kyc.answersReceivedAt" class="text-subtle">
          Svar mottagna {{ formatDateIfValid(kyc.answersReceivedAt) }}
        </span>
        <span v-else-if="counts[kyc.kycId]" class="text-subtle">{{ counts[kyc.kycId] }}</span>
        <AppButton
          v-if="kyc.status === 'awaiting_answers'"
          class="fill-in"
          color="blue"
          @click="openKycForm(kyc.kycId)"
        >
          Fyll i KYC
        </AppButton>
      </div>
      <KycAnswersView v-if="kyc.status !== 'awaiting_answers'" :answers="kyc.answers" />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { AppButton, AppChip, useModal, type ChipColor } from '@staff/ui'
import { formatDateIfValid } from '@staff/shared'

import KycAnswersView from '@/components/kyc/KycAnswersView.vue'
import KycFormModal from '@/components/kyc/KycFormModal.vue'
import { useMetadata } from '@/composables/useMetadata'
import { useKycAnswersCount } from '@/composables/kycAnswersCount'
import type { DistributorKycItem, DistributorKycStatus } from '@/services/kyc'

const props = defineProps<{
  kycs: DistributorKycItem[]
  userId: string
  onKycSubmitted: () => unknown
}>()

const { translateRef } = useMetadata()
const { openModal } = useModal()
// Only a KYC still waiting on answers has a count worth showing; the others show their answers.
const { counts, refresh } = useKycAnswersCount(() =>
  props.kycs.filter((kyc) => kyc.status === 'awaiting_answers').map((kyc) => kyc.kycId),
)

const statusChipColor = (status: DistributorKycStatus): ChipColor =>
  status === 'awaiting_answers' ? 'warning' : status === 'processing' ? 'brand' : 'muted'

const openKycForm = (kycId: string) => {
  openModal(
    'Kundkännedom',
    KycFormModal,
    {
      kycId,
      userId: props.userId,
      onSubmitted: async () => {
        await props.onKycSubmitted()
        // The count follows in the background, so the form closes without waiting for it.
        void refresh(kycId)
      },
    },
    { size: 'large' },
  )
}
</script>

<style scoped lang="scss">
.kyc-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  margin: 0;
  padding: 0;
  list-style: none;
}

.kyc-row {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  padding: var(--space-16);
  border: 1px solid var(--color-border-muted);
  border-radius: var(--radius-md);

  &[data-awaiting='true'] {
    border-color: var(--color-warning-emphasis);
    background-color: var(--color-warning-subtle);
  }
}

.kyc-row-header {
  display: flex;
  align-items: center;
  gap: var(--space-12);
}

.fill-in {
  margin-left: auto;
}
</style>
